// NOTE: please keep this JSON config sorted alphabetically to keep consistency between region config files

import {
  PaymentMethod,
  SUPPORTED_COUNTRY,
  SUPPORTED_LANGUAGE,
  MaxAgeExpiration,
} from 'utils/constants';
import { OrderSearchOperationName } from 'ui/forms/OrderLookUp/types';
import { commonConfig } from 'sites/common/config';

import { commonFieldsProps } from '../common/fields';
import { SiteConfig } from '../types';
import { usPostalCodeValidation } from '../us/validations';
import { usStates } from '../us/states';

import { CAForms } from './forms';
import { caPostalCodeValidation } from './validations';
import { caStates } from './states';

export const config: SiteConfig = {
  ...commonConfig,
  addressCountries: [
    {
      label: 'United States',
      value: SUPPORTED_COUNTRY.US,
    },
    {
      label: 'Canada',
      value: SUPPORTED_COUNTRY.CA,
    },
  ],
  addressFormatOneLiner:
    '@{salutation} @{firstName} @{lastName}, @{streetNumber} @{address1}, @{city}, @{postalCode} @{stateCode} @{countryCode}',
  addressFormatOneLinerWithAddress2:
    '@{salutation} @{firstName} @{lastName}, @{streetNumber} @{address1}, @{address2}, @{city}, @{postalCode} @{stateCode} @{countryCode}',
  addressSearch: {
    billing: ['CA', 'US'],
    shipping: ['CA'],
  },
  addressStates: {
    CA: caStates,
    US: usStates,
  },
  analytics: {
    releaseVersion: `North America | ${process.env.VERSION}`,
  },
  applePaySupportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
  badgeIdsExcludedBySaleStatus: [],
  bloomreach: {
    objectName: 'br_data',
    seoObjectName: 'br_data_6251',
    viewId: 'ca',
  },
  branchAnalyticsData: {
    agencyId: '1000075349268124316',
  },
  charactersNeededToSubmitSearch: 1,
  countryCode: 'ca',
  countryName: 'Canada',
  currency: {
    code: 'CAD',
    decimalsToDisplay: 2,
    symbol: 'CAD $',
  },
  defaultPaymentMethod: PaymentMethod.CREDIT_CARD,
  emarsysMerchantId: '1A34C1421884D313',
  enableEstimatedDeliverDatesByItem: true,
  fieldProps: commonFieldsProps,
  flag: '🇨🇦',
  forms: { ...CAForms },
  liveChat: {
    code: 'pumacagr',
    provider: 'ada',
    script: 'https://static.ada.support/embed2.js',
  },
  maxAgeCookiesExpires: MaxAgeExpiration.WEEKLY,
  maxCountRecordKeeper: 0,
  myAccountDashboardConfiguration: [
    {
      childOrder: [
        'profileComponent',
        'passwordComponent',
        'preferencesComponent',
        'ambassadorComponent',
      ],
      name: 'leftColumn',
    },
    {
      childOrder: ['addressBookComponent', 'orderHistoryComponent'],
      name: 'rightColumn',
    },
  ],
  orderLookUpQuery: OrderSearchOperationName['SearchOrderByEmailPostcode'],
  orderedPaymentPartners: [
    'visa',
    'master',
    'americanExpress',
    'payPal',
    'afterPay',
    'klarna',
    'applePay',
    'googlePay',
  ],
  paymentMethodsMixedWithGiftCard: [
    PaymentMethod.CREDIT_CARD,
    PaymentMethod.AFTERPAY,
    PaymentMethod.KLARNA_PAYMENTS,
  ],
  phoneAnchorNumberInCheckout: 'tel:18885657862',
  phoneTextNumberInCheckout: '1-888-565-PUMA (7862)',
  pixleeId: {
    en: '1233838',
    fr: '1988018',
  },
  priceInputMaxLength: 4,
  priceSliderInterval: 10,
  pumaAppDownloadUrl: 'https://app.puma.com/web-download',
  region: 'na',
  regionCode: 'PNA',
  savedCreditCardTemplate: ['new', 'saved'],
  searchEngineIndexableLanguages: [
    SUPPORTED_LANGUAGE.EN,
    SUPPORTED_LANGUAGE.FR,
  ],
  searchSymbolsMinValue: 2,
  selectablePaymentMethods: [
    PaymentMethod.AFTERPAY,
    PaymentMethod.AMBASSADOR_CARD,
    PaymentMethod.CREDIT_CARD,
    PaymentMethod.KLARNA_PAYMENTS,
  ],
  stateFieldLabels: {
    CA: {
      label: 'provinceOrTerritory',
      placeholder: 'selectProviceOrTerritory',
    },
  },
  staticFeatures: {
    ...commonConfig.staticFeatures,
    allowGuestAccessToOrderDetails: false,
    canRemoveWishlistItemsFromPDP: false,
    displayPromoDiscountBreakdown: false,
    enableModelMeasurements: true,
    enablePopularity: true,
    enableRrdTracking: true,
    giftWithPurchaseNewDesign: true,
    isPNARegion: true,
    multipleModelMeasurementEnabled: true,
    popoverLoginEnabled: true,
    productFilterCollapsible: false,
    quickAddressFinder: true,
    removeReturnIframe: true,
    returnsOnOrderDetailsPage: false,
    shapeSecurity: true,
    showAddAddressButtonInAccountDashboard: false,
    showCookieBanner: true,
    showCookieConsentLayer: true,
    showFinancialIncentivesLink: false,
    showFreeShippingCost: true,
    showFreeShippingProgressBar: true,
    showHeaderSubHeaderProductDetail: true,
    showModifiedResetPassFormPopup: false,
    showPaymentPartnersInFooter: false,
    showPhoneInCheckout: true,
    showPinterestFooterIcon: true,
    showPriceDifference: true,
    showPromoExclusionMsgAboveProductDetails: true,
    showPromoExclusionMsgInBaseSize: true,
    showTimeZoneName: true,
    singInInConfirmationForm: false,
    triggerCartTotalEvent: true,
    useMasterIdsInCartRecommenders: true,
    useNewPaymentForm: true,
  },
  supportedLocales: ['en-ca', 'fr-ca'],
  validation: {
    address: {
      length: commonConfig.validation.address.length,
      line1:
        '([0-9]+[\\u0000-~\\u0080-ž\'"]+$)|([\\u0000-~\\u0080-ž\'"]+[0-9]+$)|[p|P][\\s]*[o|O][\\s]*[b|B][\\s]*[o|O][\\s]*[x|X][\\s]*[a-zA-Z0-9]*|\\b[P|p]+(OST|ost|o|O)?\\.?\\s*[O|o|0]+(ffice|FFICE)?\\.?\\s*[B|b][O|o|0]?[X|x]+\\.?\\s+[#]?(\\d+)*(\\D+)*\\b',
    },
    companyName: {
      length: {
        max: 50,
        min: 0,
      },
    },
    email:
      "^[a-zA-Z0-9.!#$%&'*+\\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$",
    name: {
      length: commonConfig.validation.name.length,
    },
    phone: {
      length: {
        max: 30,
        min: commonConfig.validation.phone.length.min,
      },
      regex:
        '^(?:[\\s.()-]*(1|\\+1|001)[\\s.()-]*)?[\\s.()-]*[2-9][0-9]{2}[\\s.()-]*[2-9][0-9]{2}[\\s.()-]*[0-9]{4}[\\s.()-]*$',
    },
    postalCode: {
      CA: caPostalCodeValidation,
      US: usPostalCodeValidation,
    },
  },
};
